import type { UploadedImageResult, UploadMetadata } from '@sortera/dvfv-image-utils'
import {
  compressImage as compressImageImplementation,
  convertHeicToJpg as convertHeicToJpgImplementation,
  ERROR_NAME_MAX_UPLOAD_SIZE_EXCEEDED,
  isHeic as isHeicImplementation,
  MAX_IMAGE_UPLOAD_SIZE,
  uploadImages as uploadImagesImplementation,
} from '@sortera/dvfv-image-utils'
import { ensureError, formatBytes } from '@sortera/ee-utils'

import { http } from '@/integrations/http/index.ts'
import { track, trackError } from '@/services/analytics.ts'
import { t } from '@/services/translation/index.ts'

import type { CompressImage, ConvertHeicToJpg, Files, IsHeic, UploadImages } from './types.ts'

// eslint-disable-next-line func-style --- want to assign type to fn
export const uploadImages: UploadImages = async (files, meta, options) => {
  trackInitiatedUpload(meta, files)

  try {
    const images = await uploadImagesImplementation(files, meta, {
      cdnUrl: import.meta.env.VITE_IMAGE_CDN_URL,
      http,
      hooks: {
        onBeforeImageUploadSent: trackBeforeImageUploadSent,
        onError: trackImageUploadFailed,
      },
      disableCompression: options?.disableCompression,
    })

    trackImageUploadSucceeded(images)

    return images
  } catch (err) {
    const error = ensureError(err)
    if (error.name === ERROR_NAME_MAX_UPLOAD_SIZE_EXCEEDED) {
      alert(t('image-upload.error.max-size-exceeded', { maxSize: formatBytes(MAX_IMAGE_UPLOAD_SIZE) }))
    } else {
      alert(t('image-upload.error.unknown-error', { errorMessage: error.message }))
    }
    throw error
  }
}

// eslint-disable-next-line func-style --- want to assign type to fn
export const compressImage: CompressImage = image =>
  compressImageImplementation(image, {
    hooks: {
      onBeforeCompression: trackBeforeCompression,
      onCompressionSucceeded: trackCompressionSucceeded,
      onError: trackCompressionFailed,
    },
  })

// eslint-disable-next-line func-style --- want to assign type to fn
export const convertHeicToJpg: ConvertHeicToJpg = file =>
  convertHeicToJpgImplementation(file, {
    hooks: {
      onBeforeConversion: trackBeforeConversion,
      onConversionSucceeded: trackConversionSucceeded,
      onError: trackConversionFailed,
    },
  })

// eslint-disable-next-line func-style --- want to assign type to fn
export const isHeic: IsHeic = file =>
  isHeicImplementation(file, {
    hooks: {
      onError: trackIsHeicFailed,
    },
  })

// TRACKING

function trackInitiatedUpload(meta: UploadMetadata, files: Files): void {
  track('Image Upload: Initialized', {
    Meta: meta,
    Count: files.length,
  })
}

function trackBeforeCompression(file: File | Blob): void {
  track('Image Upload: Needs Compression', {
    Size: file.size,
    Type: file.type,
  })
}

function trackCompressionSucceeded(file: File | Blob, remainingRetries: number): void {
  track('Image Upload: Compression Succeeded', {
    Size: file.size,
    Type: file.type,
    RemainingRetries: remainingRetries,
  })
}

function trackCompressionFailed(error: Error, file: File | Blob, remainingRetries: number): void {
  const payload = {
    Size: file.size,
    Type: file.type,
    ErrorName: error.name,
    ErrorMessage: error.message,
    RemainingRetries: remainingRetries,
  }
  trackError(error, payload)
  track('Image Upload: Compression Failed', payload)
}

function trackBeforeImageUploadSent(files: Files): void {
  track('Image Upload: About To Upload', {
    Files: [...files].map(({ size, type }) => ({ size, type })),
  })
}

function trackImageUploadSucceeded(images: UploadedImageResult[]): void {
  track('Image Upload: Upload Succeeded', {
    Images: images,
  })
}

function trackImageUploadFailed(error: Error, files: Files, remainingRetries: number): void {
  const payload = {
    Files: [...files].map(({ size, type }) => ({ size, type })),
    ErrorName: error.name,
    ErrorMessage: error.message,
    RemainingRetries: remainingRetries,
  }
  trackError(error, payload)
  track('Image Upload: Upload Failed', payload)
}

function trackBeforeConversion(file: File): void {
  track('Image Upload: Needs Heic To Jpg Conversion', {
    Size: file.size,
    Type: file.type,
  })
}

function trackConversionSucceeded(file: File): void {
  track('Image Upload: Heic To Jpg Conversion Succeeded', {
    Size: file.size,
    Type: file.type,
  })
}

function trackConversionFailed(error: Error, file: File): void {
  const payload = {
    Size: file.size,
    Type: file.type,
    ErrorName: error.name,
    ErrorMessage: error.message,
  }
  trackError(error, payload)
  track('Image Upload: Heic To Jpg Conversion Failed', payload)
}

function trackIsHeicFailed(error: Error, file: File): void {
  const payload = {
    Size: file.size,
    Type: file.type,
    ErrorName: error.name,
    ErrorMessage: error.message,
  }
  trackError(error, payload)
  track('Image Upload: Determine Heic Failed', payload)
}
